import React, { useState, useRef } from 'react';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';

const SYMBOLS = ['🍒', '🍋', '🍊', '🍇', '⭐', '💎', '7️⃣'];
const BETS = [10, 25, 50, 100, 250, 500, 1000, 2500, 5000];

const PAYTABLE = [
  { emoji: '7️⃣', label: 'Seven',   multi: 50  },
  { emoji: '💎', label: 'Diamond', multi: 25  },
  { emoji: '⭐', label: 'Star',    multi: 10  },
  { emoji: '🍇', label: 'Grape',   multi: 5   },
  { emoji: '🍊', label: 'Orange',  multi: 4   },
  { emoji: '🍋', label: 'Lemon',   multi: 3   },
  { emoji: '🍒', label: 'Cherry',  multi: 2   },
];

function Reel({ symbol, spinning }) {
  return (
    <div className={`w-24 h-24 sm:w-32 sm:h-32 bg-slate-900 border-2 rounded-xl flex items-center justify-center text-5xl sm:text-6xl select-none transition-all duration-150 ${
      spinning ? 'border-yellow-400 animate-pulse' : 'border-slate-600'
    }`}>
      {spinning ? SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)] : symbol}
    </div>
  );
}

export default function Slot() {
  const { updateBalance } = useAuth();
  const [reels, setReels] = useState(['🎰', '🎰', '🎰']);
  const [bet, setBet] = useState(50);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const intervalRef = useRef(null);

  async function handleSpin() {
    if (spinning) return;
    setSpinning(true);
    setResult(null);
    setError('');

    // Animáció – véletlenszerű szimbólumok villognak
    intervalRef.current = setInterval(() => {
      setReels(SYMBOLS.sort(() => Math.random() - 0.5).slice(0, 3));
    }, 80);

    try {
      const res = await api.post('/games/slot/spin', { bet });
      const data = res.data;

      // Animáció leállítása, eredmény beállítása
      setTimeout(() => {
        clearInterval(intervalRef.current);
        setSpinning(false);
        setReels(data.reels.map(r => r.emoji));
        setResult(data);
        updateBalance(data.balance);
      }, 800);
    } catch (err) {
      clearInterval(intervalRef.current);
      setSpinning(false);
      setReels(['🎰', '🎰', '🎰']);
      setError(err.response?.data?.error || 'Hiba történt');
    }
  }

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 py-10">
        <h1 className="text-2xl font-bold mb-8 text-center">🎰 Slot Machine</h1>

        {/* Gép */}
        <div className="card flex flex-col items-center gap-6">
          {/* Reels */}
          <div className={`flex gap-3 p-4 bg-slate-950 rounded-2xl border-2 transition-colors ${
            result?.isWin ? 'border-yellow-400 shadow-lg shadow-yellow-500/20' : 'border-slate-700'
          }`}>
            {reels.map((sym, i) => (
              <Reel key={i} symbol={sym} spinning={spinning} />
            ))}
          </div>

          {/* Eredmény */}
          {result && !spinning && (
            <div className={`text-center px-6 py-3 rounded-xl font-bold text-lg ${
              result.isWin
                ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40'
                : 'bg-red-500/10 text-red-400 border border-red-500/20'
            }`}>
              {result.isWin
                ? `🎉 Nyertél! +${Number(result.payout).toLocaleString('hu')} 💰 (x${result.reels[0].multiplier})`
                : `😢 Nem nyertél. -${Number(bet).toLocaleString('hu')} 💰`}
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-2 text-sm">
              {error}
            </div>
          )}

          {/* Tét választó */}
          <div className="w-full">
            <p className="text-slate-400 text-sm mb-2 text-center">Tét</p>
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              {BETS.map((b) => (
                <button
                  key={b}
                  onClick={() => setBet(b)}
                  className={`shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                    bet === b
                      ? 'bg-yellow-500 text-slate-900'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {b.toLocaleString('hu')}
                </button>
              ))}
            </div>
          </div>

          {/* Spin gomb */}
          <button
            onClick={handleSpin}
            disabled={spinning}
            className="btn-gold text-lg px-12 py-3 disabled:opacity-50"
          >
            {spinning ? '🎰 Pörög...' : '🎰 SPIN'}
          </button>
        </div>

        {/* Kifizetési táblázat */}
        <div className="card mt-6">
          <h2 className="font-semibold mb-3 text-slate-300">Kifizetési táblázat</h2>
          <div className="space-y-2">
            {PAYTABLE.map((row) => (
              <div key={row.label} className="flex items-center justify-between text-sm">
                <span className="text-2xl">{row.emoji} {row.emoji} {row.emoji}</span>
                <span className="text-slate-400">{row.label}</span>
                <span className="text-yellow-400 font-semibold">x{row.multi}</span>
              </div>
            ))}
          </div>
          <p className="text-slate-500 text-xs mt-3">3 egyforma szimbólum = nyeremény</p>
        </div>
      </main>
    </div>
  );
}
